// PostDetail — full post detail page component.
//
// Renders the complete post: metadata (pin + type badge), title, byline
// (author avatar + name + "division · time ago"), an optional external-link
// card, the full ordered-block body, tags, an action bar (vote / comments /
// share), and the flat comment thread with an add-comment form.
//
// Source of truth for the layout: design/project/scripts/pages.jsx PostDetail
// (lines ~16–98) and design/project/CitiDev Hub.html (detail-wrap, detail-card,
// comments). The matching CSS lives in ./post-detail.css.
//
// Author display is resolved from the live leaderboard directory via the
// shared useAuthors hook — the same approach the feed uses — so the byline
// shows a real name + initials + division instead of a raw author_id.

import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client";
import {
  Avatar,
  Icon,
  TYPE_META,
  timeAgo as timeAgoAtom,
  type DesignPostType,
} from "@/features/hub-feed/design-atoms";
import { useAuthors } from "@/features/hub-feed/useAuthors";
import { useProfile } from "@/lib/useProfile";
import { PostModeration } from "@/features/admin-moderation/PostModeration";
import type { BodyBlock, PostType, ProjectType } from "./api";
import { updatePost } from "./api";
import { ComposePost } from "./ComposePost";
import { toggleVote } from "./VoteControl";
import { createComment, deleteComment, editComment } from "./CommentThread";
import { ReactionBar } from "./ReactionBar";
import type { ReactionSummary } from "./reactions-api";
import { GifPicker } from "./GifPicker";
import type { GifResult } from "./gifs-api";
import { copyText } from "@/lib/clipboard";
import { isSafeHttpUrl } from "@/lib/url";
import "./post-detail.css";

// ---------------------------------------------------------------------------
// API types (mirrors api/schemas/posts.py PostDetailResponse + CommentItem)
// ---------------------------------------------------------------------------

export interface CommentItem {
  id: number;
  author_id: string | null;
  /** Resolved from the Member profile (AC3, story: comments). */
  author_name?: string | null;
  /** Two-letter initials resolved from the Member profile. */
  author_initials?: string | null;
  /** Division badge resolved from the Member profile (NBS | NFS | NDS | SS). */
  author_division?: string | null;
  text: string;
  vote_count: number;
  created_at: string; // ISO 8601
  /** Set only when the comment's text/GIF was edited after creation. */
  updated_at?: string | null;
  /** Optional attached GIF (Giphy) — an addition to the comment, not a replacement. */
  gif_url?: string | null;
  gif_title?: string | null;
  reactions?: ReactionSummary[];
}

export interface PostDetailData {
  id: string;
  type: PostType;
  author_id: string | null;
  division: string;
  title: string;
  excerpt: string;
  tags: string[];
  vote_count: number;
  pinned: boolean;
  created_at: string; // ISO 8601
  /** Set only when the post's content was edited after creation. */
  updated_at?: string | null;
  /** Project lifecycle stage (poc | pilot | production) — project posts only. */
  status?: "poc" | "pilot" | "production" | null;
  external_url: string | null;
  /** What kind of thing this project showcases — project posts only. */
  project_type: ProjectType | null;
  /** Whether this post is opted into the Hub Feed (project posts: deliberate opt-in). */
  posted_to_feed?: boolean;
  /** GitHub (or other) repository URL for project posts. */
  repo_url: string | null;
  /** Rideshare platform link for project posts (may be null). */
  rideshare_url?: string | null;
  /** OpenGraph preview metadata unfurled from the post's link (may be null). */
  og_title?: string | null;
  og_description?: string | null;
  og_image_url?: string | null;
  og_site_name?: string | null;
  /** True when the current user has upvoted this post (per-user flag). */
  voted?: boolean;
  reactions?: ReactionSummary[];
  body: BodyBlock[];
  comments: CommentItem[];
}

// ---------------------------------------------------------------------------
// API fetch helper
// ---------------------------------------------------------------------------

export async function fetchPostDetail(postId: string): Promise<PostDetailData> {
  return apiClient<PostDetailData>(`/api/v1/posts/${encodeURIComponent(postId)}`);
}

// ---------------------------------------------------------------------------
// Hook: usePostDetail
// ---------------------------------------------------------------------------

interface UsePostDetailResult {
  data: PostDetailData | null;
  loading: boolean;
  error: Error | null;
  notFound: boolean;
  /** Re-fetch the post — used after an edit to show the persisted state. */
  reload: () => void;
}

export function usePostDetail(postId: string): UsePostDetailResult {
  const [data, setData] = useState<PostDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    fetchPostDetail(postId)
      .then((result) => {
        setData(result);
      })
      .catch((e: unknown) => {
        if (e && typeof e === "object" && "status" in e && (e as { status: number }).status === 404) {
          setNotFound(true);
        } else {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [postId]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, notFound, reload: load };
}

const PROJECT_STATUS_LABELS = {
  poc: "POC",
  pilot: "Pilot",
  production: "Production",
} as const;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Type badge for the detail-meta row — mirrors design/project/scripts/ui.jsx
 *  TypeBadge (.tbadge). Reuses the shared TYPE_META + Icon atoms the feed uses
 *  so the type glyph + label stay consistent across feed and detail. */
function TypeBadge({ type }: { type: PostType }) {
  const tm = TYPE_META[type as DesignPostType] ?? TYPE_META.post;
  const TIc = Icon[tm.icon];
  return (
    <span className="pd-tbadge" aria-label={`Type: ${tm.label}`}>
      <TIc size={14} /> {tm.label}
    </span>
  );
}

/** Renders ordered body blocks. List-item lines (starting with "- ") are
 *  grouped into a block of .li items; other lines are <p> elements.
 *  Mirrors renderBody() from design/project/scripts/pages.jsx. */
function BodyRenderer({ blocks }: { blocks: BodyBlock[] }) {
  const sorted = [...blocks].sort((a, b) => a.position - b.position);
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = (key: string) => {
    if (listItems.length > 0) {
      elements.push(
        <div key={`list-${key}`} style={{ margin: "4px 0 16px" }}>
          {listItems.map((item, i) => (
            <div className="pd-li" key={i}>
              {item}
            </div>
          ))}
        </div>,
      );
      listItems = [];
    }
  };

  sorted.forEach((block, i) => {
    if (block.text.startsWith("- ")) {
      listItems.push(block.text.slice(2));
    } else {
      flushList(String(i));
      elements.push(<p key={i}>{block.text}</p>);
    }
  });
  flushList("end");

  return (
    <div className="detail-body" data-testid="post-body">
      {elements}
    </div>
  );
}

/** LinkPreviewCard — a social-style unfurl card for a link, rendered when the
 *  backend was able to fetch OpenGraph metadata. Shows the og:image banner,
 *  site name, og:title, og:description, and the URL. Falls back gracefully:
 *  the caller only renders this when at least one field is present, and a
 *  broken image hides itself so the text card still reads. */
function LinkPreviewCard({
  href,
  title,
  description,
  imageUrl,
  siteName,
}: {
  href: string;
  title?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  siteName?: string | null;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  let hostname = "";
  try {
    hostname = new URL(href).hostname;
  } catch {
    hostname = href;
  }
  return (
    <a
      className="pd-preview"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="link-preview-card"
    >
      {imageUrl && !imgFailed && isSafeHttpUrl(imageUrl) && (
        <div className="pd-preview__media">
          <img src={imageUrl} alt="" loading="lazy" onError={() => setImgFailed(true)} />
        </div>
      )}
      <div className="pd-preview__body">
        <div className="pd-preview__site">
          <Icon.link size={13} /> {siteName || hostname}
        </div>
        {title && <div className="pd-preview__title">{title}</div>}
        {description && <div className="pd-preview__desc">{description}</div>}
        <div className="pd-preview__url">
          {hostname} <Icon.external size={13} />
        </div>
      </div>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Not-found / error states
// ---------------------------------------------------------------------------

function PostNotFound() {
  return (
    <div className="pd-not-found" data-testid="post-not-found" role="alert">
      <h2 className="pd-not-found__heading">Post not found</h2>
      <p className="pd-not-found__body">
        This post does not exist or may have been removed.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PostDetail — main component
// ---------------------------------------------------------------------------

interface Props {
  /** The post id to fetch and render. */
  postId: string;
  /** Called when the user clicks "Back to the hub". */
  onBack?: () => void;
}

/**
 * PostDetail renders the full detail view for a single post, matching the
 * design/project/scripts/pages.jsx PostDetail layout pixel-for-pixel.
 *
 * Back link → detail-card (meta + title + byline + optional link card + body +
 * tags + actions) → comments section (form + thread).
 *
 * Light and dark mode are handled by the design tokens in index.css; per-view
 * classes live in ./post-detail.css.
 */
export function PostDetail({ postId, onBack }: Props) {
  const { data: post, loading, error, notFound, reload } = usePostDetail(postId);
  const authors = useAuthors();
  const { isAdmin, profile } = useProfile();
  const currentUserId = profile?.id ?? null;

  // Post edit mode — the author (or an admin) may edit the post in place.
  const [editingPost, setEditingPost] = useState(false);

  // Vote state (toggle via the existing votes API).
  const [voted, setVoted] = useState(false);
  const [voteCount, setVoteCount] = useState(0);
  const [voteBusy, setVoteBusy] = useState(false);

  // Pinned state, mirrored locally so an admin's pin/unpin toggle (via
  // PostModeration) reflects immediately without a full reload.
  const [pinned, setPinned] = useState(false);
  // Project lifecycle status, mirrored locally so an admin's status change
  // (via PostModeration) reflects immediately without a full reload.
  const [status, setStatus] = useState<"poc" | "pilot" | "production" | null>(null);

  // Comment state — seeded from the loaded post, appended optimistically.
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // GIF staged in the composer via GifPicker — an addition to the comment,
  // not a replacement for it; text is still required to submit.
  const [stagedGif, setStagedGif] = useState<GifResult | null>(null);

  // Comment edit state — the author (or an admin) may edit a comment in place.
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentDraft, setEditCommentDraft] = useState("");
  const [editCommentBusy, setEditCommentBusy] = useState(false);

  // Comment delete state — the author (or an admin) may delete a comment.
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);

  // Share state — copies a deep link to the post and briefly flips the label.
  const [shared, setShared] = useState(false);

  // Clone-command copy state — briefly flips the button label to "Copied!".
  const [cloneCopied, setCloneCopied] = useState(false);

  // Sync local state when the post finishes loading / changes. The `voted`
  // state is seeded from the post's per-user flag so the user's own upvote
  // persists across reloads.
  useEffect(() => {
    if (post) {
      setVoted(post.voted ?? false);
      setVoteCount(post.vote_count);
      setComments(post.comments);
      setPinned(post.pinned);
      setStatus(post.status ?? null);
    }
  }, [post]);

  const handleVote = useCallback(async () => {
    if (!post || voteBusy) return;
    const prevVoted = voted;
    const prevCount = voteCount;
    const nextVoted = !voted;
    setVoted(nextVoted);
    setVoteCount(nextVoted ? voteCount + 1 : voteCount - 1);
    setVoteBusy(true);
    try {
      const res = await toggleVote("post", post.id);
      setVoted(res.voted);
      setVoteCount(res.vote_count);
    } catch {
      setVoted(prevVoted);
      setVoteCount(prevCount);
    } finally {
      setVoteBusy(false);
    }
  }, [post, voted, voteCount, voteBusy]);

  // Copy a shareable link to the clipboard. When the post carries a link we
  // share that URL; otherwise a deep link back into the hub (?post=<id>).
  // Dependency-free; gives brief "Copied!" feedback for ~1.5s.
  const handleShare = useCallback(async () => {
    if (!post) return;
    const url = post.external_url
      ? post.external_url
      : `${window.location.origin}/?post=${encodeURIComponent(post.id)}`;
    if (await copyText(url)) {
      setShared(true);
      window.setTimeout(() => setShared(false), 1500);
    }
  }, [post]);

  const handleCopyClone = useCallback(async () => {
    if (!post?.repo_url) return;
    if (await copyText(`git clone ${post.repo_url}`)) {
      setCloneCopied(true);
      window.setTimeout(() => setCloneCopied(false), 1500);
    }
  }, [post]);

  const submitComment = useCallback(async () => {
    if (!post) return;
    const text = draft.trim();
    // A comment needs content — text or an attached GIF. A GIF alone is allowed.
    if ((!text && !stagedGif) || submitting) return;
    setSubmitting(true);
    try {
      const created = await createComment(post.id, {
        text,
        gif_url: stagedGif?.url ?? null,
        gif_title: stagedGif?.title ?? null,
      });
      setComments((prev) => [
        ...prev,
        {
          id: created.id,
          author_id: created.author_id,
          author_name: created.author_name,
          author_initials: created.author_initials,
          author_division: created.author_division,
          text: created.text,
          vote_count: created.vote_count,
          created_at: created.created_at,
          gif_url: created.gif_url,
          gif_title: created.gif_title,
          reactions: created.reactions ?? [],
        },
      ]);
      setDraft("");
      setStagedGif(null);
    } catch {
      // Best-effort: leave the draft in place so the user can retry.
    } finally {
      setSubmitting(false);
    }
  }, [post, draft, submitting, stagedGif]);

  const beginEditComment = useCallback((c: CommentItem) => {
    setEditingCommentId(c.id);
    setEditCommentDraft(c.text);
  }, []);

  const cancelEditComment = useCallback(() => {
    setEditingCommentId(null);
    setEditCommentDraft("");
  }, []);

  const saveEditComment = useCallback(
    async (c: CommentItem) => {
      if (!post) return;
      const text = editCommentDraft.trim();
      // Text may be emptied only when the comment keeps an attached GIF.
      if ((!text && !c.gif_url) || editCommentBusy) return;
      setEditCommentBusy(true);
      try {
        // Preserve any attached GIF — this edit affordance revises text only.
        const updated = await editComment(post.id, c.id, {
          text,
          gif_url: c.gif_url ?? null,
          gif_title: c.gif_title ?? null,
        });
        setComments((prev) =>
          prev.map((x) =>
            x.id === c.id ? { ...x, text: updated.text, updated_at: updated.updated_at } : x,
          ),
        );
        setEditingCommentId(null);
        setEditCommentDraft("");
      } catch {
        // Best-effort: keep the edit form open so the user can retry.
      } finally {
        setEditCommentBusy(false);
      }
    },
    [post, editCommentDraft, editCommentBusy],
  );

  const handleDeleteComment = useCallback(
    async (c: CommentItem) => {
      if (!post || deletingCommentId != null) return;
      if (!window.confirm("Delete this comment? This action cannot be undone.")) return;
      setDeletingCommentId(c.id);
      try {
        await deleteComment(post.id, c.id);
        setComments((prev) => prev.filter((x) => x.id !== c.id));
        if (editingCommentId === c.id) {
          setEditingCommentId(null);
          setEditCommentDraft("");
        }
      } catch {
        // Best-effort: leave the comment in place so the user can retry.
      } finally {
        setDeletingCommentId(null);
      }
    },
    [post, deletingCommentId, editingCommentId],
  );

  if (loading) {
    return (
      <div className="pd-loading" role="status" aria-live="polite" data-testid="post-detail-loading">
        Loading post…
      </div>
    );
  }

  if (notFound) {
    return <PostNotFound />;
  }

  if (error || !post) {
    return (
      <div className="pd-error" role="alert" data-testid="post-detail-error">
        <strong>Could not load this post.</strong>
        <p>{error?.message ?? "An unexpected error occurred."}</p>
      </div>
    );
  }

  // Resolve the author_id → display name / initials / division via the live
  // leaderboard directory, falling back to the post's own division and the
  // raw id when the directory has not yet loaded (or has no entry).
  const author = post.author_id ? authors[post.author_id] : undefined;
  const authorName = author?.name ?? post.author_id ?? "Unknown";
  const authorDivision = author?.division ?? post.division;
  const avatarId = post.author_id ?? "anon";

  // The author may edit their own post; an admin may edit any post (the API
  // enforces this too — this is only the UI gate).
  const canEditPost =
    isAdmin || (currentUserId != null && currentUserId === post.author_id);

  if (editingPost) {
    return (
      <div className="detail-wrap fade-in" data-testid="post-detail">
        <button type="button" className="backlink" onClick={onBack} data-testid="back-link">
          <Icon.arrowL size={16} /> Back to the hub
        </button>
        <div className="detail-card" data-testid="post-edit-card">
          <h2 style={{ marginTop: 0 }}>Edit post</h2>
          <ComposePost
            initial={{
              type: post.type,
              title: post.title,
              excerpt: post.excerpt,
              body: post.body,
              tags: post.tags,
              external_url: post.external_url,
              project_type: post.project_type,
              repo_url: post.repo_url,
              rideshare_url: post.rideshare_url,
              status: post.status ?? undefined,
              posted_to_feed: post.posted_to_feed,
            }}
            submitOverride={(payload) => updatePost(post.id, payload)}
            onSuccess={() => {
              setEditingPost(false);
              reload();
            }}
            onCancel={() => setEditingPost(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="detail-wrap fade-in" data-testid="post-detail">
      <button type="button" className="backlink" onClick={onBack} data-testid="back-link">
        <Icon.arrowL size={16} /> Back to the hub
      </button>

      <article className="detail-card" data-testid="detail-card">
        <div className="detail-meta">
          {pinned && (
            <span className="pin" aria-label="Pinned post">
              <Icon.pin size={12} /> Pinned
            </span>
          )}
          <TypeBadge type={post.type} />
          {post.type === "project" && status && (
            <span
              className={`pd-status-badge pd-status-badge--${status}`}
              data-testid="project-status-badge"
            >
              {PROJECT_STATUS_LABELS[status]}
            </span>
          )}
          {canEditPost && (
            <button
              type="button"
              className="pd-edit-btn"
              data-testid="post-edit-button"
              onClick={() => setEditingPost(true)}
              aria-label="Edit this post"
            >
              Edit
            </button>
          )}
          {isAdmin && (
            <PostModeration
              postId={post.id}
              isPinned={pinned}
              postType={post.type}
              status={status}
              onPinChange={(_id, nextPinned) => setPinned(nextPinned)}
              onStatusChange={(_id, nextStatus) => setStatus(nextStatus)}
              onRemove={() => onBack?.()}
            />
          )}
        </div>

        <h1 data-testid="post-title">{post.title}</h1>

        <div className="detail-byline" data-testid="post-byline">
          <Avatar id={avatarId} initials={author?.initials} name={author?.name} avatar={author?.avatar} size={42} />
          <div style={{ lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap" }}>{authorName}</div>
            <div style={{ fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>
              {authorDivision} · {timeAgoAtom(post.created_at)}
              {post.updated_at && (
                <span data-testid="post-updated-at"> · Updated {timeAgoAtom(post.updated_at)}</span>
              )}
            </div>
          </div>
        </div>

        {isSafeHttpUrl(post.external_url) &&
          (post.og_title || post.og_image_url || post.og_description ? (
            <LinkPreviewCard
              href={post.external_url as string}
              title={post.og_title}
              description={post.og_description}
              imageUrl={post.og_image_url}
              siteName={post.og_site_name}
            />
          ) : (
            <a
              className="detail-linkcard"
              href={post.external_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="external-link-card"
            >
              <span style={{ color: "var(--green-deep)" }}>
                <Icon.link size={20} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="pd-u">{post.external_url}</div>
                <div className="pd-l">External link · opens in a new tab</div>
              </div>
              <Icon.external size={17} />
            </a>
          ))}

        {isSafeHttpUrl(post.repo_url) && (
          <div className="detail-repocard" data-testid="repo-card">
            <a
              className="detail-linkcard"
              href={post.repo_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="repo-link"
            >
              <span style={{ color: "var(--green-deep)" }}>
                <Icon.link size={20} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="pd-u">{post.repo_url}</div>
                <div className="pd-l">Repository · opens in a new tab</div>
              </div>
              <Icon.external size={17} />
            </a>
            <button
              type="button"
              className={"pd-actbtn" + (cloneCopied ? " on" : "")}
              onClick={handleCopyClone}
              data-testid="repo-clone"
              aria-label="Copy git clone command"
            >
              <Icon.package size={15} /> {cloneCopied ? "Copied!" : "Copy git clone"}
            </button>
          </div>
        )}

        {isSafeHttpUrl(post.rideshare_url) && (
          <div className="detail-repocard" data-testid="rideshare-card">
            <a
              className="detail-linkcard"
              href={post.rideshare_url ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rideshare-link"
            >
              <span style={{ color: "var(--green-deep)" }}>
                <Icon.link size={20} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="pd-u">{post.rideshare_url}</div>
                <div className="pd-l">NBS Rideshare · opens in a new tab</div>
              </div>
              <Icon.external size={17} />
            </a>
          </div>
        )}

        <BodyRenderer blocks={post.body} />

        {post.tags.length > 0 && (
          <div className="fr-tags" style={{ marginTop: 20 }} data-testid="post-tags">
            {post.tags.map((t) => (
              <span key={t} className="chip">
                #{t}
              </span>
            ))}
          </div>
        )}

        <div className="detail-actions" data-testid="post-actions">
          <button
            type="button"
            className={"pd-actbtn" + (voted ? " on" : "")}
            onClick={handleVote}
            disabled={voteBusy}
            aria-pressed={voted}
            aria-label={`${voteCount} upvotes`}
          >
            <Icon.up size={15} /> {voteCount} {voteCount === 1 ? "upvote" : "upvotes"}
          </button>
          <button
            type="button"
            className={"pd-actbtn" + (shared ? " on" : "")}
            aria-label="Share this post"
            onClick={handleShare}
          >
            <Icon.share size={15} /> {shared ? "Copied!" : "Share"}
          </button>
          <ReactionBar target="post" targetId={post.id} initialReactions={post.reactions ?? []} />
        </div>
      </article>

      <section className="pd-comments" data-testid="comments-section" aria-label="Comments">
        <h3>
          Comments <span className="pd-n">{comments.length}</span>
        </h3>

        <div className="pd-cform">
          <Avatar id={avatarId} initials={author?.initials} name={author?.name} avatar={author?.avatar} size={36} />
          <div style={{ flex: 1 }}>
            <textarea
              data-testid="comment-textarea"
              placeholder="Add a comment — share what you tried, ask a question…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submitComment();
              }}
            />
            {stagedGif && (
              <div className="gif-staged" data-testid="staged-gif">
                <img src={stagedGif.preview_url} alt={stagedGif.title} />
                <button
                  type="button"
                  className="gif-staged__remove"
                  aria-label="Remove attached GIF"
                  onClick={() => setStagedGif(null)}
                  data-testid="staged-gif-remove"
                >
                  ×
                </button>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 9 }}>
              <GifPicker onSelect={setStagedGif} />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={submitComment}
                disabled={(!draft.trim() && !stagedGif) || submitting}
                data-testid="comment-submit"
                style={{ opacity: (draft.trim() || stagedGif) && !submitting ? 1 : 0.5 }}
              >
                {submitting ? "Posting…" : "Comment"}
              </button>
            </div>
          </div>
        </div>

        {comments.map((c) => {
          const cName = c.author_name ?? c.author_id ?? "Unknown";
          const canEditComment =
            isAdmin || (currentUserId != null && currentUserId === c.author_id);
          const isEditingThis = editingCommentId === c.id;
          return (
            <div className="pd-comment" data-testid="comment-row" data-comment-id={c.id} key={c.id}>
              <Avatar id={c.author_id ?? "anon"} initials={c.author_initials ?? undefined} name={c.author_name ?? undefined} avatar={c.author_id ? authors[c.author_id]?.avatar : undefined} size={36} />
              <div>
                <div className="cmeta">
                  <b>{cName}</b>
                  {c.author_division && (
                    <span className="t">{c.author_division}</span>
                  )}
                  <span className="t">{timeAgoAtom(c.created_at)}</span>
                  {c.updated_at && (
                    <span className="t" data-testid="comment-edited"> · edited</span>
                  )}
                </div>
                {isEditingThis ? (
                  <div className="pd-comment-edit" data-testid="comment-edit-form">
                    <textarea
                      className="pd-comment-edit__input"
                      data-testid="comment-edit-textarea"
                      value={editCommentDraft}
                      onChange={(e) => setEditCommentDraft(e.target.value)}
                      rows={3}
                      aria-label="Edit comment"
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => saveEditComment(c)}
                        disabled={(!editCommentDraft.trim() && !c.gif_url) || editCommentBusy}
                        data-testid="comment-edit-save"
                      >
                        {editCommentBusy ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={cancelEditComment}
                        disabled={editCommentBusy}
                        data-testid="comment-edit-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  c.text && <div className="ctext">{c.text}</div>
                )}
                {c.gif_url && isSafeHttpUrl(c.gif_url) && (
                  <img
                    className="pd-comment-gif"
                    src={c.gif_url}
                    alt={c.gif_title ?? "Attached GIF"}
                    loading="lazy"
                    data-testid="comment-gif"
                  />
                )}
                <div className="cfoot">
                  <span className="pd-cvote" aria-label={`${c.vote_count} votes`}>
                    <Icon.up size={12} /> {c.vote_count}
                  </span>
                  <ReactionBar
                    target="comment"
                    targetId={String(c.id)}
                    initialReactions={c.reactions ?? []}
                  />
                  {canEditComment && !isEditingThis && (
                    <button
                      type="button"
                      className="pd-comment-edit-btn"
                      data-testid="comment-edit-button"
                      onClick={() => beginEditComment(c)}
                      aria-label="Edit this comment"
                    >
                      Edit
                    </button>
                  )}
                  {canEditComment && (
                    <button
                      type="button"
                      className="pd-comment-edit-btn"
                      data-testid="comment-delete-button"
                      onClick={() => handleDeleteComment(c)}
                      disabled={deletingCommentId === c.id}
                      aria-label="Delete this comment"
                    >
                      {deletingCommentId === c.id ? "Deleting…" : "Delete"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}

export default PostDetail;
